import {
  BridgeMessage,
  MessageDetails,
  MessageType,
  NavigationRequest,
} from "@shared/services/webview/types";
import { Notification } from "@shared/types";
import * as H from "history";
import { useEffect, useRef, useState } from "react";
import {
  postMessageToNative,
  userUpdated,
} from "services/webview/webViewBridgeUtils";
import { useHistory, useLocation } from "react-router-dom";
import { useNotificationNavigation } from "features/notifications/hooks.ts";
import { openMessage } from "utils/misc.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useSelector } from "react-redux";
import { IS_WEB_VIEW } from "common/constants.ts";
import { shouldBlockNavigation } from "utils/hooks/useBackButtonHandler";
import { historyStack } from "utils/hooks/useHistoryTracker.ts";

export const useWebViewBridge = () => {
  const history = useHistory();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const historyRef = useRef<H.History<unknown> | undefined>(history);
  const locationRef = useRef<H.Location<unknown> | undefined>(location);
  historyRef.current = history;
  locationRef.current = location;
  const { navigateFromNotification } = useNotificationNavigation();
  const realUser = useSelector(
    sharedLib.selectors.authentication.selectRealUser
  );

  useEffect(() => {
    userUpdated(realUser).catch(() => {});
  }, [realUser]);

  useEffect(() => {
    if (isInitialized || !IS_WEB_VIEW) {
      return;
    }

    window.dispatchWebViewEvent = handleNativeMessage;
    postMessageToNative({ type: MessageType.WEBVIEW_READY });
    setIsInitialized(true);
  }, [isInitialized]);

  const handleNativeMessage = (message: BridgeMessage<any>): void => {
    const { type, payload } = message;

    switch (type) {
      case MessageType.NAVIGATE_BACK_REQUEST:
        if (!shouldBlockNavigation() && historyStack.length > 1) {
          historyRef.current?.push(historyStack[historyStack.length - 2], {
            isBackButton: true,
          });
        }
        break;

      case MessageType.NAVIGATION_REQUEST:
        const pathname = (payload as NavigationRequest)?.pathname;
        if (pathname) historyRef.current?.push(pathname);
        break;

      case MessageType.OPEN_MESSAGE:
        const messageDetails = payload as MessageDetails;
        if (messageDetails) {
          openMessage(messageDetails.text, messageDetails.type);
        }
        break;

      case MessageType.NOTIFICATION_NAVIGATION_REQUEST:
        navigateFromNotification(payload as Notification);
        break;

      // Add other handlers for messages pushed from Native
      default:
        console.warn(
          `WebView (React): Unhandled message type from Native: ${type}`
        );
    }
  };

  return {
    isInitialized,
  };
};
